import React, { useRef, useState } from 'react';
import axios from 'axios';
import { AlertTriangle, Download, Loader2, Upload, X } from 'lucide-react';
import { API_BASE_URL } from '../config';
import { useAuth, useToast } from '../contexts/AppContexts';

const API = `${API_BASE_URL}/api`;

/**
 * AgencyBackupRestore — password-gated agency archive utility.
 *
 * Props:
 *  - agency: { id, name } | null    (null => "All Agencies" mode for Super Admin)
 *  - onClose: () => void
 */
const AgencyBackupRestore = ({ agency, onClose }) => {
  const { user, token } = useAuth();
  const toast = useToast();
  const [mode, setMode] = useState('backup'); // 'backup' | 'restore'
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [restoreFile, setRestoreFile] = useState(null);
  const fileInputRef = useRef(null);
  const isAllAgencies = !agency;

  const canAll = user?.role === 'Super Admin';
  if (isAllAgencies && !canAll) {
    return null;
  }

  const backupPath = isAllAgencies
    ? `${API}/agencies/backup-all`
    : `${API}/agencies/${agency.id}/backup`;
  const restorePath = isAllAgencies
    ? `${API}/agencies/restore-all`
    : `${API}/agencies/${agency.id}/restore`;

  const doBackup = async () => {
    if (!password) {
      toast.error('Enter your password to continue');
      return;
    }
    setBusy(true);
    setLastResult(null);
    try {
      const resp = await axios.post(
        backupPath,
        { password },
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
        }
      );
      // Try to read manifest header for a friendly summary
      let summary = null;
      try {
        summary = JSON.parse(resp.headers['x-backup-manifest'] || '{}');
      } catch (_) { /* ignore */ }
      // Filename from Content-Disposition
      const cd = resp.headers['content-disposition'] || '';
      const match = cd.match(/filename="?([^";]+)"?/i);
      const filename = match ? match[1] : `exrsim_backup_${Date.now()}.zip`;
      const url = window.URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setLastResult({
        kind: 'backup',
        filename,
        summary,
      });
      toast.success('Backup downloaded');
      setPassword('');
    } catch (err) {
      // axios with responseType blob returns error data as blob — decode it
      let detail = err.response?.statusText || err.message || 'Backup failed';
      try {
        if (err.response?.data instanceof Blob) {
          const text = await err.response.data.text();
          const j = JSON.parse(text);
          if (j.detail) detail = j.detail;
        } else if (err.response?.data?.detail) {
          detail = err.response.data.detail;
        }
      } catch (_) { /* ignore */ }
      toast.error(`Backup failed: ${detail}`);
    } finally {
      setBusy(false);
    }
  };

  const doRestore = async () => {
    if (!password) {
      toast.error('Enter your password to continue');
      return;
    }
    if (!restoreFile) {
      toast.error('Choose a backup .zip to restore');
      return;
    }
    const scope = isAllAgencies ? 'ALL AGENCIES (entire tenant)' : `"${agency.name}"`;
    if (!window.confirm(
      `Restore ${scope} from "${restoreFile.name}"?\n\n` +
      'This is upsert-by-id: existing records with the same id will be overwritten. ' +
      'New records will be inserted. This action cannot be undone.'
    )) return;

    setBusy(true);
    setLastResult(null);
    try {
      const form = new FormData();
      form.append('file', restoreFile);
      form.append('password', password);
      const resp = await axios.post(restorePath, form, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLastResult({ kind: 'restore', data: resp.data });
      toast.success('Restore complete');
      setPassword('');
      setRestoreFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || 'Restore failed';
      toast.error(`Restore failed: ${detail}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[10005] flex items-center justify-center bg-black/80 p-4" data-testid="agency-backup-modal">
      <div className="bg-gray-800 rounded-lg shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-700">
          <div>
            <h2 className="text-xl font-bold text-white">Backup &amp; Restore</h2>
            <p className="text-sm text-gray-400 mt-1">
              {isAllAgencies ? 'All Agencies (full-tenant archive)' : agency.name}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white" data-testid="backup-close-btn">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="px-5 pt-4 flex gap-2 border-b border-gray-700">
          <button
            onClick={() => { setMode('backup'); setLastResult(null); }}
            className={`px-4 py-2 text-sm font-semibold rounded-t-md ${mode === 'backup' ? 'bg-orange-600 text-white' : 'bg-gray-900 text-gray-400 hover:text-white'}`}
            data-testid="backup-tab-download"
          >
            <Download className="h-4 w-4 inline mr-2" />
            Backup / Download
          </button>
          <button
            onClick={() => { setMode('restore'); setLastResult(null); }}
            className={`px-4 py-2 text-sm font-semibold rounded-t-md ${mode === 'restore' ? 'bg-orange-600 text-white' : 'bg-gray-900 text-gray-400 hover:text-white'}`}
            data-testid="backup-tab-restore"
          >
            <Upload className="h-4 w-4 inline mr-2" />
            Restore / Upload
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 180px)' }}>
          {mode === 'backup' && (
            <>
              <p className="text-sm text-gray-300">
                Download a ZIP archive containing every record and uploaded file for
                {isAllAgencies ? ' the entire tenant.' : ` "${agency.name}".`}{' '}
                Keep it in a safe, offline location for disaster recovery.
              </p>
              <div className="bg-gray-900 border border-gray-700 rounded-md p-3 text-xs text-gray-400 space-y-1">
                <p>The archive includes:</p>
                <ul className="list-disc list-inside space-y-0.5">
                  <li>All Mongo collections scoped to {isAllAgencies ? 'every agency' : 'this agency'} (JSON per collection)</li>
                  <li>Exercise documents, policies &amp; procedures, exercise images, GFA weather images</li>
                  <li>`manifest.json` + `README.md` with restore instructions</li>
                </ul>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Confirm your password to continue</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Your account password"
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded text-white focus:outline-none focus:border-orange-400"
                  data-testid="backup-password-input"
                />
              </div>
              <button
                onClick={doBackup}
                disabled={busy || !password}
                className="w-full bg-orange-600 hover:bg-orange-700 text-white py-2 rounded font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="backup-download-btn"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                {busy ? 'Preparing archive…' : 'Download Backup ZIP'}
              </button>
              {lastResult?.kind === 'backup' && lastResult.summary && (
                <div className="bg-green-900/30 border border-green-700 rounded-md p-3 text-sm text-green-200" data-testid="backup-result">
                  <p className="font-semibold">Downloaded: {lastResult.filename}</p>
                  <p className="text-xs mt-1">
                    {lastResult.summary.collections} collections · {lastResult.summary.documents} documents · {lastResult.summary.attachments} attachments
                  </p>
                </div>
              )}
            </>
          )}

          {mode === 'restore' && (
            <>
              <div className="bg-red-900/30 border border-red-700 rounded-md p-3 text-sm text-red-200 flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-semibold">Danger zone</p>
                  <p className="text-xs mt-1">
                    Restore is upsert-by-id: existing records with the same id will be overwritten.
                    We recommend taking a fresh backup first.
                    {!isAllAgencies && ' Records outside this agency in the archive will be skipped.'}
                  </p>
                </div>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Backup archive (.zip)</label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".zip,application/zip"
                  onChange={(e) => setRestoreFile(e.target.files?.[0] || null)}
                  className="w-full text-sm text-gray-300 file:mr-3 file:py-2 file:px-3 file:rounded file:border-0 file:bg-orange-600 file:text-white file:hover:bg-orange-700"
                  data-testid="restore-file-input"
                />
                {restoreFile && (
                  <p className="text-xs text-gray-400 mt-1">
                    {restoreFile.name} · {(restoreFile.size / (1024 * 1024)).toFixed(2)} MB
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Confirm your password to continue</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Your account password"
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded text-white focus:outline-none focus:border-orange-400"
                  data-testid="restore-password-input"
                />
              </div>
              <button
                onClick={doRestore}
                disabled={busy || !password || !restoreFile}
                className="w-full bg-red-600 hover:bg-red-700 text-white py-2 rounded font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="restore-run-btn"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                {busy ? 'Restoring…' : 'Restore from Backup'}
              </button>
              {lastResult?.kind === 'restore' && (
                <div className="bg-green-900/30 border border-green-700 rounded-md p-3 text-sm text-green-200 space-y-2" data-testid="restore-result">
                  <p className="font-semibold">Restore complete</p>
                  <div className="text-xs">
                    <p>Attachments restored: {lastResult.data.attachments_restored}</p>
                    {lastResult.data.attachments_failed?.length > 0 && (
                      <p className="text-yellow-300">Attachment failures: {lastResult.data.attachments_failed.length}</p>
                    )}
                    <p className="mt-2 font-semibold">Documents restored:</p>
                    <ul className="list-disc list-inside">
                      {Object.entries(lastResult.data.restored_counts || {}).map(([k, v]) => (
                        <li key={k}>{k}: {v}</li>
                      ))}
                    </ul>
                    {Object.keys(lastResult.data.skipped_out_of_scope || {}).length > 0 && (
                      <>
                        <p className="mt-2 font-semibold text-yellow-300">Skipped (outside this agency):</p>
                        <ul className="list-disc list-inside text-yellow-300">
                          {Object.entries(lastResult.data.skipped_out_of_scope).map(([k, v]) => (
                            <li key={k}>{k}: {v}</li>
                          ))}
                        </ul>
                      </>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AgencyBackupRestore;
